// Ferry markers (EU 561/2006 Art. 9) are what let a work day end at a rest far
// shorter than the eight-hour gap: a rest interrupted by driving onto or off a
// boat still counts as one rest. The markers are FERRY_START / FERRY_END points
// on the tachograph, so a crossing is a pair — and a marker the tachograph
// never closed is exactly the case that makes a day split look unexplained.

import { addMinutes, parseISO } from 'date-fns'

export interface FerryMarker {
  id: string
  type: string
  timestamp: string
}

export interface FerrySegment {
  id: string
  start: Date
  /** null when no counterpart marker closes this one. */
  end: Date | null
  /** Which half is missing, for a marker that never got its pair. */
  unpaired: 'start' | 'end' | null
}

// A crossing of a few seconds is a real thing to see (it is usually a driver
// toggling the symbol by mistake), but it draws as nothing. Segments are given
// at least this much width; the tooltip carries the true times.
export const MIN_RENDERED_SEGMENT_MINUTES = 5

const FERRY_START = 'FERRY_START'
const FERRY_END = 'FERRY_END'

/**
 * Pair ferry markers into crossings, in time order.
 *
 * A START opens a crossing and the next END closes it. A START while one is
 * already open leaves the earlier one unpaired rather than swallowing it, and
 * an END with nothing open is unpaired too — both are worth seeing.
 */
export const buildFerrySegments = (
  markers: readonly FerryMarker[]
): FerrySegment[] => {
  const ordered = [...markers]
    .map(marker => ({ marker, at: parseISO(marker.timestamp) }))
    .sort((a, b) => a.at.getTime() - b.at.getTime())

  const segments: FerrySegment[] = []
  let open: { id: string; at: Date } | null = null

  const pushOpenAsUnpaired = () => {
    if (!open) return
    segments.push({
      id: open.id,
      start: open.at,
      end: null,
      unpaired: 'end',
    })
    open = null
  }

  for (const { marker, at } of ordered) {
    if (marker.type === FERRY_START) {
      pushOpenAsUnpaired()
      open = { id: marker.id, at }
      continue
    }
    if (marker.type !== FERRY_END) continue

    if (open) {
      segments.push({ id: open.id, start: open.at, end: at, unpaired: null })
      open = null
    } else {
      segments.push({ id: marker.id, start: at, end: null, unpaired: 'start' })
    }
  }

  pushOpenAsUnpaired()

  return segments.sort((a, b) => a.start.getTime() - b.start.getTime())
}

/** The span a segment is drawn across, widened so a short one stays visible. */
export const ferrySegmentDisplayBounds = (segment: FerrySegment) => {
  const floor = addMinutes(segment.start, MIN_RENDERED_SEGMENT_MINUTES)
  const end = segment.end && segment.end > floor ? segment.end : floor
  return { startAt: segment.start, endAt: end }
}
